import { Server, Cpu, Box, Database, AlertTriangle } from 'lucide-react';
import type { ClusterHealthLive } from '@/types';

interface Props {
  health: ClusterHealthLive | undefined;
  isLoading: boolean;
}

function StatusCard({
  icon: Icon,
  label,
  value,
  sub,
  ok,
}: {
  icon: typeof Server;
  label: string;
  value: string;
  sub?: string;
  ok: boolean;
}) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Icon size={16} className={ok ? 'text-status-healthy' : 'text-status-error'} />
        <span className="text-xs font-medium text-silver-muted uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-silver-bright">{value}</div>
      {sub && <div className="text-xs text-silver-muted">{sub}</div>}
    </div>
  );
}

export default function ClusterHealthPanel({ health, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-surface border border-surface-elevated rounded-xl p-4 h-24 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!health) return null;

  const warningEvents = health.podsFailed + health.podsPending;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatusCard
          icon={Server}
          label="API Server"
          value={health.kubernetesReachable ? `${health.apiServerLatencyMs}ms` : 'Unreachable'}
          sub={health.kubernetesReachable ? 'Latency' : 'Connection failed'}
          ok={health.kubernetesReachable}
        />
        <StatusCard
          icon={Cpu}
          label="Nodes"
          value={`${health.nodesReady}/${health.nodesTotal}`}
          sub="Ready"
          ok={health.nodesReady === health.nodesTotal && health.nodesTotal > 0}
        />
        <StatusCard
          icon={Box}
          label="Pods"
          value={`${health.podsRunning}/${health.podsTotal}`}
          sub={warningEvents > 0 ? `${health.podsFailed} failed, ${health.podsPending} pending` : 'Running'}
          ok={health.podsFailed === 0}
        />
        <StatusCard
          icon={Database}
          label="etcd"
          value={health.etcdHealthy ? `${health.etcdMemberCount} members` : 'Unhealthy'}
          sub={health.etcdHealthy ? 'Healthy' : 'Check connectivity'}
          ok={health.etcdHealthy}
        />
      </div>

      {warningEvents > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-status-warning/10 border border-status-warning/30 rounded-lg">
          <AlertTriangle size={14} className="text-status-warning" />
          <span className="text-xs text-status-warning">
            {health.podsFailed} failed and {health.podsPending} pending pods
          </span>
        </div>
      )}

      {health.nodesPressure?.length > 0 && (
        <div className="space-y-1 px-4 py-2 bg-status-error/10 border border-status-error/30 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-status-error" />
            <span className="text-xs font-medium text-status-error">Node Pressure</span>
          </div>
          {health.nodesPressure.map((p, i) => (
            <div key={i} className="text-xs text-status-error">
              <span className="font-mono font-medium">{p.node}</span>: {p.type}{p.message ? ` — ${p.message}` : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
